import { useState, useEffect, useCallback } from 'react';
import { exportCsv, dateStamp } from '../services/exportCsv';

/**
 * RobotsPage — register robots without manual MySQL inserts. Super admins only.
 *
 * Talks to the evesix-operators API (Cloudflare Worker → Hyperdrive → MySQL
 * `robot` table in robotdb). A registered robot is immediately joinable to
 * sessions/analytics under its hospital.
 */

const API_BASE = import.meta.env.VITE_OPERATORS_API_URL || 'https://evesix-operators-dev.reshigan-085.workers.dev';

// The operators worker verifies this portal login token against the auth
// worker and requires the Super Admin role.
async function api(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('portal_auth_token') || ''}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.success === false) {
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return body;
}

export default function RobotsPage({ user }) {
  const [robots, setRobots] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [created, setCreated] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [r, h] = await Promise.all([api('/robots'), api('/hospitals')]);
      setRobots(r.data || []);
      setHospitals(h.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const q = query.trim().toLowerCase();
  const visible = q
    ? robots.filter((r) =>
        [r.Serial, r.Hospital_Name, r.version, r.reference]
          .some((v) => (v || '').toLowerCase().includes(q))
      )
    : robots;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Robot Fleet</h1>
          <p className="text-sm text-slate-500 mt-1">
            {q ? `${visible.length} of ${robots.length} robots` : `${robots.length} robots`} · registered robots report under their hospital immediately
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() =>
              exportCsv(`evesix-robots-${dateStamp()}.csv`, [
                { key: 'Serial', label: 'Serial' },
                { key: 'Hospital_Name', label: 'Hospital' },
                { key: 'Hospital_Id', label: 'Hospital ID' },
                { key: 'version', label: 'Version' },
                { key: 'reference', label: 'Reference' },
              ], visible)
            }
            disabled={loading || visible.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-sm font-semibold rounded-xl border border-slate-200 shadow-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4" />
            </svg>
            Export CSV{q ? ` (${visible.length})` : ''}
          </button>
          <button
            onClick={() => { setCreated(null); setShowForm(true); }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Register Robot
          </button>
        </div>
      </div>

      {created && (
        <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start justify-between gap-4">
          <p className="text-emerald-800 font-semibold">
            ✅ Robot <span className="font-mono">{created.Serial}</span> registered at {created.Hospital_Name}
          </p>
          <button onClick={() => setCreated(null)} className="text-emerald-500 hover:text-emerald-700 text-xl leading-none">×</button>
        </div>
      )}

      <div className="mb-4">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by serial, hospital, version…"
          className="w-full sm:w-96 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 caret-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-4">{error}</div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-5 py-3">Serial</th>
              <th className="px-5 py-3">Hospital</th>
              <th className="px-5 py-3">Version</th>
              <th className="px-5 py-3">Reference</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-400">Loading robots…</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-400">No robots match your search.</td></tr>
            ) : visible.map((r) => (
              <tr key={r.Serial} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                <td className="px-5 py-3 font-mono font-semibold text-slate-800">{r.Serial}</td>
                <td className="px-5 py-3 text-slate-700">{(r.Hospital_Name || '').trim() || <span className="text-slate-400">unassigned</span>}</td>
                <td className="px-5 py-3"><span className="inline-block px-2.5 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs">{r.version || '—'}</span></td>
                <td className="px-5 py-3 text-slate-600">{r.reference || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <AddRobotModal
          hospitals={hospitals}
          onClose={() => setShowForm(false)}
          onCreated={(r) => { setShowForm(false); setCreated(r); load(); }}
        />
      )}
    </div>
  );
}

function AddRobotModal({ hospitals, onClose, onCreated }) {
  const [form, setForm] = useState({ serial: '', hospital_id: '', version: 'V1.6', reference: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await api('/robots', {
        method: 'POST',
        body: JSON.stringify({
          serial: form.serial,
          hospital_id: Number(form.hospital_id),
          version: form.version,
          reference: form.reference || undefined,
        }),
      });
      onCreated(res.data);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 backdrop-blur-sm p-4 pt-20" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Register Robot</h2>
            <p className="text-xs text-slate-500 mt-0.5">Adds the robot to the robot database — no manual SQL needed</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">×</button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <Field label="Serial number *">
            <input
              required value={form.serial} onChange={set('serial')} className={inputCls + ' font-mono uppercase'}
              placeholder="e.g. EVE20058" pattern="[A-Za-z0-9_-]{3,30}"
            />
          </Field>

          <Field label="Hospital *">
            <select required value={form.hospital_id} onChange={set('hospital_id')} className={inputCls}>
              <option value="" disabled>Select hospital…</option>
              {hospitals.map((h) => (
                <option key={h.Hospital_Id} value={h.Hospital_Id}>{(h.Hospital_Name || '').trim()}</option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Version">
              <input value={form.version} onChange={set('version')} className={inputCls} placeholder="V1.6" />
            </Field>
            <Field label="Reference (optional)">
              <input value={form.reference} onChange={set('reference')} className={inputCls} placeholder="internal ref" />
            </Field>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{error}</div>}

          <div className="flex justify-end gap-3 pt-2 pb-1">
            <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-800">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl shadow-sm"
            >
              {saving ? 'Registering…' : 'Register robot'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls = 'w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 caret-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
